import {html, LitElement} from "../libs/lit.dist.js";
import {getGlobalStyleSheets, WebElement} from "./abstract-element.js";

const ALERTS_TYPES_CONFIG = {
    primary: {
        alertClass: 'alert-primary',
        iconColor: 'color-primary',
        icon: `fa-info-circle`,
        textColor: '',
    },
    secondary: {
        alertClass: 'alert-secondary',
        iconColor: 'fal fa-info-circle',
        icon: `fa-info-circle`,
        textColor: '',
    },
    danger: {
        alertClass: 'alert-danger',
        iconColor: 'color-danger-900',
        icon: `fa-exclamation-circle`,
        textColor: 'color-danger-900',
    },
    success: {
        alertClass: 'alert-success',
        iconColor: 'color-success-900',
        icon: `fa-check-circle`,
        textColor: 'color-success-900',
    },
    info: {
        alertClass: 'alert-info',
        iconColor: 'color-info-900',
        icon: `fa-info-circle`,
        textColor: 'color-info-900',
    },
    warning: {
        alertClass: 'alert-warning',
        iconColor: 'color-warning-900',
        icon: `fa-exclamation-triangle`,
        textColor: 'color-warning-900',
    },
    light: {
        alertClass: 'alert-light',
        iconColor: '',
        icon: `fa-info-circle`,
        textColor: '',
    },
    dark: {
        alertClass: 'alert-dark',
        iconColor: '',
        icon: `fa-info-circle`,
        textColor: '',
    },
};

/**
 * Element <pk-alert [type="String] [warning] [info] [danger] [success]>[treść]</pk-alert>
 *
 * Atrybuty:
 * {String}  type        - Typ alertu ["warning", "info", "danger", "success", "primary", "secondary"]
 * {boolean} warning     - Ustawia typ alertu na 'warning'
 * {boolean} success     - Ustawia typ alertu na 'success'
 * {boolean} info        - Ustawia typ alertu na 'info'
 * {boolean} danger      - Ustawia typ alertu na 'danger'
 * {string}  size        - Rozmiar alertu ["md", "xs"] (domyślnie "md")
 * {string}  className   - Dodatkowe klasy CSS
 * {boolean} disableIcon - Czy ukryć ikonę alertu (domyślnie false)
 *
 * Ex.
 * <pk-alert warning>Uwaga: foo</pk-alert>
 */
export class PkAlert extends LitElement {

    static styles = [getGlobalStyleSheets()];

    static properties = {
        type: {type: String},
        icon: {type: String},
        info: {type: Boolean},
        danger: {type: Boolean},
        warning: {type: Boolean},
        success: {type: Boolean},
        secondary: {type: Boolean},
        light: {type: Boolean},
        dark: {type: Boolean},
        size: {type: String},
        content: {type: String},
        className: {type: String},
        disableIcon: {type: Boolean},
    }

    constructor(props) {
        super(props);
        this.info = false;
        this.size = 'md';
        this.type = '';
        this.icon = '';
        this.iconType = POLKURIER?.FA_ICON_TYPE || 'fal';
        this.content = '';
        this.className = '';
        this.disableIcon = false;
    }

    getType() {
        if (this.type) {
            return this.type;
        }

        switch (true) {
            case this.danger:
                return 'danger';

            case this.warning:
                return 'warning';

            case this.success:
                return 'success';

            case this.info:
                return 'info';

            case this.secondary:
                return 'secondary';

            case this.light:
                return 'light';

            case this.dark:
                return 'dark';

            default:
                return 'primary';
        }
    }

    render() {
        const type = this.getType();
        const data = ALERTS_TYPES_CONFIG[type] || ALERTS_TYPES_CONFIG.primary || {};
        const dismissible = this.dismissible;

        let iconSizeClass = 'icon-stack-md';
        const cssClass = ['alert', this.className, data.alertClass];
        if (dismissible) {
            cssClass.push('alert-dismissible');
        }

        if (this.size === 'xs') {
            cssClass.push('p-1 m-0');
            iconSizeClass = 'icon-stack-xs';
        } else {
            cssClass.push('mb-3');
        }

        return html`
            <div class="${cssClass.join(' ')}" role="alert">
                <div class="d-flex align-items-baseline">
                    ${!this.disableIcon ? html`
                        <div class="alert-icon text-center mr-2 me-2">
                           <span class="icon-stack ${iconSizeClass}">
                               <i class="fa-fw ${this.iconType} ${this.icon || data.icon } ${data.iconColor}"></i>
                           </span>
                        </div>
                    ` : ''}
                    <div class="flex-1">
                        <span class="${data.textColor}">
                            <slot>
                                ${this.content || ''}
                            </slot>
                        </span>
                    </div>
                </div>
            </div>
        `;
    }
}

if (!customElements.get('pk-alert')) {
    customElements.define('pk-alert', PkAlert);
}


/**
 * Wyświetla grupy alertów z tablicy.
 *
 * Element:
 * <pk-alerts [type="string"] [warning] [info] [danger] [success] [group] alerts='array'></pk-alerts>
 *
 * Atrybuty:
 *  Dziedziczy pozostałe atrybuty z <pk-alert>
 *  {String[]|Object[]} alerts - Alerty do wyświetlenia
 *  {boolean}  showEmpty - Czy wyświetlić mimo braku alertów (domyślnie false)
 *  {boolean}  group     - Czy zgrupować alerty tego samego typu (domyślnie false)
 *
 * Ex.
 * <pk-alerts warning alerts='["foo", "bar", {"type": "danger", "content": "Vaz"}]'></pk-alerts>
 */
class PkAlerts extends WebElement {

    static get properties() {
        return {
            ...PkAlert.properties,
            showEmpty: {type: Boolean},
            group: {type: Boolean},
            alerts: {type: Array},
            dismissible: {type: Boolean},
        };
    }

    constructor() {
        super();
        this.showEmpty = false;
        this.group = false;
        this.dismissible = false;
        this.alerts = [];
    }

    getType() {
        return PkAlert.prototype.getType.call(this);
    }

    render() {
        if (this.showEmpty !== true && (!this.alerts || this.alerts.length === 0)) {
            return html``;
        }
        return html`${this.group === true ? this.renderGroupedByType() : this.renderUngrouped()}`;
    }

    renderUngrouped() {
        const type = this.getType();
        return _.map(this.alerts, alert => html`
            <pk-alert type=${alert.type || type} .dismissible=${this.dismissible}>
                <span>${alert.content || alert}</span>
            </pk-alert>
        `);
    }

    renderGroupedByType() {
        const type = this.getType();
        const groups = _.groupBy(this.alerts, m => m.type || type);
        return _.keys(groups).map(type =>  html`
            <pk-alert type=${type} .dismissible=${this.dismissible}>
                <ul class="m-0 ${groups[type].length === 1 ? 'list-unstyled' : ''}">
                    ${_.map(groups[type], alert => html`
                        <li>${alert.content || alert}</li>
                    `)}
                </ul>
            </pk-alert>
        `);

    }

}

if (!customElements.get('pk-alerts')) {
    customElements.define('pk-alerts', PkAlerts);
}
